interface Track {
  id: string;
  title: string;
}

interface Playlist {
  name: string;
  tracks: Track[];
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

class RadioElement extends HTMLElement {
  private shadow: ShadowRoot;
  private audio: HTMLAudioElement;
  private playlist: Playlist | null = null;
  private queue: Track[] = [];
  private queueIndex = 0;
  private expanded = false;
  private state: "idle" | "loading" | "playing" | "paused" = "idle";

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
    this.audio = new Audio();
    this.audio.preload = "none";
  }

  static get observedAttributes(): string[] {
    return ["data-source", "theme", "position"];
  }

  async connectedCallback(): Promise<void> {
    const src =
      this.getAttribute("data-source") ||
      "https://kerry.ink/widgets/radio/playlist.json";
    await this.loadPlaylist(src);
    this.render();
    this.setupAudioHandlers();
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", () => this.render());
  }

  disconnectedCallback(): void {
    this.audio.pause();
    this.audio.src = "";
  }

  private async loadPlaylist(src: string): Promise<void> {
    try {
      const res = await fetch(src);
      const data = await res.json();
      this.playlist = data;
      this.queue = shuffle([...(data.tracks || [])]);
      this.queueIndex = 0;
    } catch (e) {
      console.error("[radio-widget] Failed to load playlist", e);
    }
  }

  private get currentTrack(): Track | null {
    return this.queue[this.queueIndex] ?? null;
  }

  private loadTrack(track: Track): void {
    this.audio.src = `https://cdn1.suno.ai/${track.id}.mp3`;
    this.state = "loading";
    this.render();
    this.audio.play().then(() => {
      this.state = "playing";
      this.render();
    }).catch(() => {
      this.state = "paused";
      this.render();
    });
  }

  private nextTrack(): void {
    this.queueIndex++;
    if (this.queueIndex >= this.queue.length) {
      this.queue = shuffle([...(this.playlist?.tracks || [])]);
      this.queueIndex = 0;
    }
    if (this.currentTrack) this.loadTrack(this.currentTrack);
  }

  private prevTrack(): void {
    this.queueIndex = Math.max(0, this.queueIndex - 1);
    if (this.currentTrack) this.loadTrack(this.currentTrack);
  }

  private togglePlay(): void {
    if (this.state === "idle" || this.state === "paused") {
      if (!this.audio.src && this.currentTrack) {
        this.loadTrack(this.currentTrack);
      } else {
        this.audio.play().then(() => {
          this.state = "playing";
          this.render();
        });
      }
    } else if (this.state === "playing") {
      this.audio.pause();
      this.state = "paused";
      this.render();
    }
  }

  private setupAudioHandlers(): void {
    this.audio.addEventListener("ended", () => this.nextTrack());
    this.audio.addEventListener("error", () => {
      this.state = "paused";
      this.render();
    });
    this.audio.addEventListener("play", () => {
      this.state = "playing";
      this.render();
      this.syncMediaSession();
    });
    this.audio.addEventListener("pause", () => {
      this.state = "paused";
      this.render();
      if (navigator.mediaSession) navigator.mediaSession.playbackState = "paused";
    });

    if ("mediaSession" in navigator) {
      navigator.mediaSession.setActionHandler("play", () => this.togglePlay());
      navigator.mediaSession.setActionHandler("pause", () => this.togglePlay());
      navigator.mediaSession.setActionHandler("previoustrack", () => this.prevTrack());
      navigator.mediaSession.setActionHandler("nexttrack", () => this.nextTrack());
    }
  }

  private syncMediaSession(): void {
    if (!("mediaSession" in navigator)) return;
    const track = this.currentTrack;
    if (track) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: track.title,
        artist: this.playlist?.name ?? "97.5 KRRY",
      });
    }
    navigator.mediaSession.playbackState = "playing";
  }

  private resolveTheme(): "dark" | "light" {
    const attr = this.getAttribute("theme") || "auto";
    if (attr === "auto") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }
    return attr as "dark" | "light";
  }

  private isLeft(): boolean {
    return (this.getAttribute("position") ?? "bottom-right").includes("left");
  }

  private isTop(): boolean {
    return (this.getAttribute("position") ?? "bottom-right").includes("top");
  }

  private render(): void {
    const isDark = this.resolveTheme() === "dark";
    const track = this.currentTrack;
    const isPlaying = this.state === "playing";
    const isLoading = this.state === "loading";
    const isActive = isPlaying || isLoading || this.state === "paused";

    const posLeft = this.isLeft();
    const posTop = this.isTop();

    const eqBars = isPlaying
      ? `<span class="eq">
          <span class="eq__bar" style="animation-delay:0s"></span>
          <span class="eq__bar" style="animation-delay:0.15s"></span>
          <span class="eq__bar" style="animation-delay:0.3s"></span>
        </span>`
      : `<span class="note">♫</span>`;

    const playBtn = isLoading
      ? `<button class="ctrl ctrl--play" aria-label="Loading" disabled>⋯</button>`
      : isPlaying
      ? `<button class="ctrl ctrl--play" aria-label="Pause">⏸</button>`
      : `<button class="ctrl ctrl--play" aria-label="Play">▶</button>`;

    const html = `
      <style>
        :host {
          display: block;
          position: fixed;
          ${posTop ? "top: 18px; bottom: auto;" : "bottom: 18px; top: auto;"}
          ${posLeft ? "left: 18px; right: auto;" : "right: 18px; left: auto;"}
          z-index: 999;
          font-family: 'SF Pro Rounded', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          --glass-bg: ${isDark ? "rgba(20, 20, 20, 0.65)" : "rgba(255, 255, 255, 0.75)"};
          --glass-border: ${isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(255, 255, 255, 0.5)"};
          --text: ${isDark ? "#e0e0e0" : "#1a1a1a"};
          --muted: ${isDark ? "#888" : "#999"};
          --accent: ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"};
          --shadow: ${isDark ? "0 8px 32px rgba(0,0,0,0.5),0 2px 8px rgba(0,0,0,0.3)" : "0 8px 32px rgba(0,0,0,0.12),0 2px 8px rgba(0,0,0,0.08)"};
          --spring: cubic-bezier(0.34, 1.26, 0.64, 1);
        }

        .widget {
          background: var(--glass-bg);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid var(--glass-border);
          box-shadow: var(--shadow);
          border-radius: 999px;
          color: var(--text);
          display: flex;
          align-items: center;
          gap: 0.4em;
          padding: 0.5em 0.75em;
          cursor: pointer;
          transition: border-radius 0.3s var(--spring), padding 0.3s var(--spring), box-shadow 0.2s ease;
          user-select: none;
          max-width: 280px;
          box-sizing: border-box;
          min-height: 40px;
          min-width: 40px;
        }

        .widget.is-expanded {
          border-radius: 20px;
          padding: 0.55em 0.85em;
        }

        .widget:hover {
          box-shadow: ${isDark ? "0 12px 48px rgba(0,0,0,0.6),0 4px 12px rgba(0,0,0,0.4)" : "0 12px 48px rgba(0,0,0,0.15),0 4px 12px rgba(0,0,0,0.1)"};
          border-color: ${isDark ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.7)"};
        }

        .note {
          font-size: 1.1em;
          flex-shrink: 0;
          opacity: 0.7;
          transition: opacity 0.2s;
        }

        .widget:hover .note {
          opacity: 1;
        }

        /* Equalizer bars */
        .eq {
          display: flex;
          align-items: flex-end;
          gap: 2px;
          height: 16px;
          flex-shrink: 0;
        }

        .eq__bar {
          width: 3px;
          background: ${isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.5)"};
          border-radius: 2px;
          animation: eq-dance 0.8s ease-in-out infinite alternate;
        }

        @keyframes eq-dance {
          from { height: 4px; }
          to   { height: 14px; }
        }

        /* Track info */
        .track-info {
          display: flex;
          flex-direction: column;
          min-width: 0;
          flex: 1;
          overflow: hidden;
        }

        .track-title {
          font-size: 0.8em;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: var(--text);
          letter-spacing: 0.01em;
          max-width: 160px;
        }

        .station-name {
          font-size: 0.65em;
          color: var(--muted);
          white-space: nowrap;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        /* Controls */
        .controls {
          display: flex;
          align-items: center;
          gap: 0.15em;
          flex-shrink: 0;
        }

        .ctrl {
          background: none;
          border: none;
          color: var(--text);
          cursor: pointer;
          font-size: 0.9em;
          padding: 0.25em 0.3em;
          border-radius: 6px;
          transition: background 0.15s ease, transform 0.1s ease;
          line-height: 1;
          min-width: 28px;
          min-height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ctrl:hover {
          background: var(--accent);
        }

        .ctrl:active {
          transform: scale(0.9);
        }

        .ctrl--play {
          font-size: 1em;
          opacity: 0.9;
        }

        .ctrl--skip {
          font-size: 0.75em;
          opacity: 0.55;
        }

        .ctrl--skip:hover {
          opacity: 0.85;
        }

        .ctrl:disabled {
          opacity: 0.4;
          cursor: default;
        }

        /* Collapsed: just the note/eq, no track info */
        .widget:not(.is-expanded) .track-info,
        .widget:not(.is-expanded) .controls {
          display: none;
        }

        @media (hover: none) {
          .widget:hover { box-shadow: var(--shadow); }
        }
      </style>

      <div class="widget${this.expanded ? " is-expanded" : ""}" id="widget">
        ${eqBars}
        <div class="track-info">
          <span class="track-title">${track ? track.title : "—"}</span>
          <span class="station-name">${this.playlist?.name ?? "Radio"}</span>
        </div>
        <div class="controls">
          <button class="ctrl ctrl--skip" id="btn-prev" aria-label="Previous">⏮</button>
          ${playBtn}
          <button class="ctrl ctrl--skip" id="btn-next" aria-label="Next">⏭</button>
        </div>
      </div>
    `;

    this.shadow.innerHTML = html;

    const widget = this.shadow.getElementById("widget");

    widget?.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.id === "btn-prev" || target.closest("#btn-prev")) {
        this.prevTrack();
      } else if (target.id === "btn-next" || target.closest("#btn-next")) {
        this.nextTrack();
      } else if (
        target.classList.contains("ctrl--play") ||
        target.closest(".ctrl--play")
      ) {
        this.togglePlay();
      } else {
        // Toggle expand/collapse on widget click
        this.expanded = !this.expanded;
        if (this.expanded && this.state === "idle" && this.currentTrack) {
          this.loadTrack(this.currentTrack);
        } else {
          this.render();
        }
      }
    });
  }
}

if (!customElements.get("radio-widget")) {
  customElements.define("radio-widget", RadioElement);
}
